import type { PaginationState } from '@tanstack/react-table';
import axios from 'axios';

import type { PaginatedListDto } from '@apple/utils/pagination/models';

import { pendingReplyCountSchema } from '../models/recent';
import type { OrderListing, OrderSearchFilters, RecentOrderListing } from '../models/search';
import { orderSearchResponseSchema, recentOrderListingSchema } from '../models/search';

export type OrderSearchRequestFilters = Omit<OrderSearchFilters, 'orderDateRangeInDays'> & {
	orderDateRangeInDays?: number;
};

export function buildOrderSearchRequestFilters(
	filters: OrderSearchFilters,
): OrderSearchRequestFilters {
	const { orderDateRangeInDays, ...requestFilters } = filters;

	return {
		...requestFilters,
		...(typeof orderDateRangeInDays === 'number' ? { orderDateRangeInDays } : {}),
	};
}

export async function searchOrders(
	pagination: PaginationState,
	filters: OrderSearchFilters,
	abortSignal?: AbortSignal,
): Promise<PaginatedListDto<OrderListing>> {
	const { data } = await axios.get<unknown>('/api/order-search/v2', {
		params: {
			...buildOrderSearchRequestFilters(filters),
			pageNumber: pagination.pageIndex + 1,
			pageSize: pagination.pageSize,
		},
		signal: abortSignal,
	});

	return orderSearchResponseSchema.parse(data).orders;
}

export async function getPendingReplyCount(
	startDate?: Date,
	abortSignal?: AbortSignal,
): Promise<number> {
	const { data } = await axios.get<unknown>('/api/order/pending-reply-count', {
		params: startDate ? { startDate } : {},
		signal: abortSignal,
	});

	return pendingReplyCountSchema.parse(data);
}

export async function getRecentOrders(
	days = 30,
	take = 20,
	abortSignal?: AbortSignal,
): Promise<RecentOrderListing[]> {
	const { data } = await axios.get<unknown>('/api/order/recent-orders', {
		params: { days, take },
		signal: abortSignal,
	});
	return recentOrderListingSchema.array().parse(data);
}

export function getOrderSearchExportUrl() {
	return '/api/order-search/export';
}
